<template>
  <section class="ac-panel" data-test="action-center" aria-label="Alerts">
    <ActionCenterChips :categories="categories" :active="category" @select="$emit('select-category', $event)" />

    <ActionCenterToolbar
      :focus-label="focusFilter.label"
      :focus-icon="focusFilter.icon"
      :focus-on="focus"
      :sort-order="sort"
      :clear-label="clearLabel"
      :can-clear="visibleCount > 0"
      @toggle-focus="$emit('toggle-focus')"
      @toggle-sort="$emit('toggle-sort')"
      @clear="$emit('clear-all')"
    />

    <div class="ac-list">
      <template v-if="groups.length">
        <section v-for="group in groups" :key="group.key" class="ac-group" :data-test="`ac-group-${group.key}`">
          <h3 class="ac-group-head">
            <span class="ac-group-label">{{ group.label }}</span>
            <span class="ac-group-rule" aria-hidden="true"></span>
            <span class="ac-group-count">{{ group.items.length }}</span>
          </h3>
          <AlertCard
            v-for="item in group.items"
            :key="item.key"
            :item="item"
            :reply-text="replyTexts[item.id] || ''"
            :reply-sending="!!replySending[item.id]"
            :busy="!!busy[item.id]"
            @primary="$emit('card-primary', item)"
            @dismiss="$emit('card-dismiss', item)"
            @reply-input="$emit('reply-input', { item, text: $event })"
            @reply-send="$emit('reply-send', item)"
            @reply-focus="$emit('reply-focus', item)"
            @reply-blur="$emit('reply-blur', item)"
            @open-step="$emit('open-step', $event)"
          />
        </section>
      </template>
      <ActionCenterEmpty v-else :icon="emptyState.icon" :title="emptyState.title" :body="emptyState.body" />
    </div>

    <ActionCenterUndoBar v-if="undoText" :text="undoText" @undo="$emit('undo')" />
  </section>
</template>

<script>
import ActionCenterChips from './ActionCenterChips.vue';
import ActionCenterToolbar from './ActionCenterToolbar.vue';
import ActionCenterEmpty from './ActionCenterEmpty.vue';
import ActionCenterUndoBar from './ActionCenterUndoBar.vue';
import AlertCard from './AlertCard.vue';

// Alerts mode of the Action Center. Presentational: AgentSidebar computes the lists
// (src/utils/actionCenterAlerts.js) and handles every emitted intent.
export default {
  name: 'ActionCenterPanel',
  components: { ActionCenterChips, ActionCenterToolbar, ActionCenterEmpty, ActionCenterUndoBar, AlertCard },
  props: {
    categories: { type: Array, required: true },
    category: { type: String, default: 'all' },
    focus: { type: Boolean, default: false },
    focusFilter: { type: Object, required: true },
    sort: { type: String, default: 'new' },
    groups: { type: Array, required: true },
    emptyState: { type: Object, required: true },
    clearLabel: { type: String, default: 'Clear all' },
    undoText: { type: String, default: '' },
    replyTexts: { type: Object, default: () => ({}) },
    replySending: { type: Object, default: () => ({}) },
    busy: { type: Object, default: () => ({}) },
  },
  computed: {
    visibleCount() {
      return this.groups.reduce((n, g) => n + g.items.length, 0);
    },
  },
};
</script>

<style lang="scss" scoped>
.ac-panel {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
}

.ac-list {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: 12px 14px 20px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  background: var(--ac-neutral-25);
}

.ac-group {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.ac-group-head {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0;
  padding: 6px 0 0;
  font-size: 11px;
  line-height: 1;
}

.ac-group-label {
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--ac-text-subtle);
}

.ac-group-rule {
  flex: 1;
  height: 1px;
  background: var(--ac-border);
}

.ac-group-count {
  font-weight: 600;
  color: var(--ac-text-subtle);
}

@media (max-width: 767.98px) {
  .ac-list {
    padding-bottom: calc(20px + env(safe-area-inset-bottom, 0px));
  }
}
</style>
